import {type DragRef} from '@wandb/weave/common/containers/DragDropContainer';
import {mediaStrings} from '@wandb/weave/common/types/media';
import {ID} from '@wandb/weave/common/util/id';
import {isTruthy} from '@wandb/weave/common/util/types';
import {toIncludesObj} from '@wandb/weave/core';
import {compact, isEqual, isNumber, sortBy} from 'lodash';

import {
  type AddPanel,
  type APIAddedPanelSpecs,
  type DefaultPanelSpecs,
  type FoundAPIAddedPanelSpecs,
  type FoundPanelSpecs,
  type PanelBankConfig,
  PanelBankConfigState,
  type PanelBankDiff,
  type PanelBankSectionConfig,
  type PanelBankSectionConfigWithVisiblePanels,
  type PanelIsActiveParams,
  panelViews,
  type QueryArgSpecs,
} from '../components/PanelBank/types';
import {type RunsLinePlotConfig} from '../components/PanelRunsLinePlot/types';
import {type VegaPanel2Config} from '../components/PanelVega2';
import {weavePanelForWeaveKey} from '../components/PanelWeave';
import {OrganizationPrefix} from '../components/WorkspaceDrawer/Settings/types';
import {type VizMap} from '../state/graphql/historyKeysQuery';
import type * as PanelTypes from '../state/views/panel/types';
import type * as PanelBankSectionConfigTypes from '../state/views/panelBankSectionConfig/types';
import {
  type RunHistoryKeyInfo,
  type RunHistoryKeyInfoKeys,
  type RunHistoryKeyType,
} from '../types/run';
import * as Panels from '../util/panels';
import * as RunHelpers from '../util/runhelpers';
import {startPerfTimer} from './datadog/profiler';
import {startFunctionPerformanceMeasure} from './datadog/workspace/functionPerformance';
import {isSystemMetric} from './metrics';
import {
  DEFAULT_X_AXIS,
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_CUSTOM_CHARTS_NAME,
  PANEL_BANK_EVALUATION_NAME,
  PANEL_BANK_EVALUATION_TABLES_NAME,
  PANEL_BANK_HIDDEN_SECTION_NAME,
  PANEL_BANK_MEDIA_NAME,
  PANEL_BANK_SYSTEM_NAME,
  PANEL_BANK_TABLES_NAME,
} from './panelbankTypes';
import {type LayedOutPanel, type Panel} from './panelTypes';
import {isReservedKey} from './runs';
import {isSparseOverridesEnabled} from './sparseOverridesFeatureFlag';
import {type QueryField} from './vega3';

const organizationPrefixIsLast = (autoOrganizePrefix?: OrganizationPrefix) =>
  autoOrganizePrefix === OrganizationPrefix.LastPrefix;

export function isPanel(ref: DragRef | null): ref is PanelTypes.Ref {
  return ref != null && ref.type === 'panel';
}

export function isHidden(
  prevKeysSet: Set<string>,
  key: string,
  panelBankConfigState: PanelBankConfigState
): boolean {
  return (
    prevKeysSet.has(key) && panelBankConfigState !== PanelBankConfigState.Init
  );
}

export function isPanelBankSection(
  ref: DragRef | null
): ref is PanelBankSectionConfigTypes.Ref {
  return ref != null && ref.type === 'panel-bank-section-config'; // o.panels != null;
}

const keyInfoToAddedPanels = (
  keyViz: {[key: string]: any},
  autoOrganizePrefix?: OrganizationPrefix,
  shouldIncludeWeavePanel?: boolean
) => {
  const addedPanels: APIAddedPanelSpecs = {};
  const setSection = (key: string) => {
    if (key.includes('/')) {
      if (organizationPrefixIsLast(autoOrganizePrefix)) {
        const lastIndex = key.lastIndexOf('/');
        return key.substring(0, lastIndex);
      } else {
        const firstIndex = key.indexOf('/');
        return key.substring(0, firstIndex);
      }
    } else {
      return PANEL_BANK_CUSTOM_CHARTS_NAME;
    }
  };
  for (const viz of Object.keys(keyViz)) {
    const panel_type = keyViz[viz].panel_type;
    const v = keyViz[viz].panel_config;
    const section = setSection(viz);
    if (v != null) {
      addedPanels[viz] = {
        type: 'api-added-panel',
        config: {...v},
        defaultSection: section,
        viewType: panel_type,
      };
    }
  }
  if (shouldIncludeWeavePanel && addedPanels['weave-trace'] == null) {
    addedPanels['weave-trace'] = {
      type: 'api-added-panel',
      viewType: panelViews.weaveTrace,
      config: {},
      defaultSection: PANEL_BANK_EVALUATION_NAME,
    };
  }
  return addedPanels;
};

// This gets called a lot. Keep it fast.
export const keyInfoToDefaultPanelSpecs = (
  keys: RunHistoryKeyInfoKeys,
  keyViz: VizMap,
  autoOrganizePrefix?: OrganizationPrefix,
  definedMetricsMap?: Panels.MetricsDict,
  hiddenMetrics?: Set<string>,
  shouldIncludeEvalTablePanels?: boolean
) => {
  // Test exact release cohorts in fixed history-key buckets with matching
  // feature flags. Duration * 1000 / keyCount gives microseconds per history
  // key; this isolates the small per-key loop improvement that can disappear
  // inside the broader expectedPanelSpecsSpan. keyCount reuses the key array
  // this function already needs instead of adding a telemetry-only scan.
  const endMeasure = startFunctionPerformanceMeasure(
    'keyInfoToDefaultPanelSpecs'
  );

  // for some available metrics, create a flattened mapping
  // of expected panel keys -> metrics

  // the resulting panel bank should contain ALL of the panels described here
  // (even if some are in the Hidden Panels section)
  // (this excludes Charlemagne panels added through wandbs _add_panel api)
  const keyTypes = RunHelpers.keyTypes(keys);
  const defaultPanelSpecs: DefaultPanelSpecs = {};
  const hasKeyViz = Object.keys(keyViz).length > 0;

  let systemMetrics = new Set<string>();
  const remainingMetrics = new Set<string>();
  const keyNames = Object.keys(keys);
  keyNames.forEach(metric => {
    if (metric.startsWith('system')) {
      systemMetrics.add(metric);
    } else {
      remainingMetrics.add(metric);
    }
  });

  for (const template of Object.values(Panels.systemPanelTemplates)) {
    const match = new Set<string>();
    const noMatch = new Set<string>();
    systemMetrics.forEach(metric => {
      if (metric.match(template.regex)) {
        match.add(metric);
      } else {
        noMatch.add(metric);
      }
    });
    if (match.size > 0) {
      defaultPanelSpecs[template.key] = {
        type: 'default-panel',
        metrics: Array.from(match),
        keyType: 'number',
        defaultSection: PANEL_BANK_SYSTEM_NAME,
      };
    }

    systemMetrics = noMatch || [];
  }

  // for the remaining systemMetrics, first look for
  // OpenMetrics and try to group them by
  // the <common_key> in <common_key>.<id>
  // For example, group together
  // system/openmetrics.DCGM.DCGM_FI_DEV_GPU_TEMP.0 and
  // system/openmetrics.DCGM.DCGM_FI_DEV_GPU_TEMP.1
  // into common_key = system/openmetrics.DCGM.DCGM_FI_DEV_GPU_TEMP

  const systemMetricsByCommonKey: {[key: string]: string[]} = {};
  for (const metric of systemMetrics) {
    if (!metric.startsWith('system/openmetrics')) {
      continue;
    }
    // commonKey is everything up to the last dot:
    const commonKey = metric.substring(0, metric.lastIndexOf('.'));
    if (systemMetricsByCommonKey[commonKey] == null) {
      systemMetricsByCommonKey[commonKey] = [];
    }
    // append to the end of the array:
    systemMetricsByCommonKey[commonKey].push(metric);
    // remove the matching metrics from systemMetrics
    systemMetrics.delete(metric);
  }

  for (const commonKey of Object.keys(systemMetricsByCommonKey)) {
    const metrics = systemMetricsByCommonKey[commonKey];
    const sortedMetrics = sortBy(metrics);
    defaultPanelSpecs[sortedMetrics[0]] = {
      type: 'default-panel',
      metrics: sortedMetrics,
      keyType: 'number',
      defaultSection: PANEL_BANK_SYSTEM_NAME,
    };
  }

  const combinedMetrics = [...systemMetrics, ...remainingMetrics];
  // the remaining metrics should all correspond to single-metric default
  // panels
  for (const metric of combinedMetrics) {
    let hasMatchingPanel = false;
    if (['_step', '_runtime', '_timestamp'].includes(metric)) {
      continue;
    }

    const keyType = keyTypes[metric];

    if (hasKeyViz && keyViz[metric]) {
      // skip cases where the visualization is defined with a config, since it is a Charlemagne
      // panel with a key identical to a metric
      if ('panel_config' in keyViz[metric]) {
        continue;
      }
      const {id: storedPanelDefId, ...props} = keyViz[metric];
      const panelDefId =
        storedPanelDefId != null &&
        !(
          storedPanelDefId.startsWith('builtin:') ||
          storedPanelDefId.startsWith('lib:')
        )
          ? `lib:${storedPanelDefId}`
          : storedPanelDefId;
      hasMatchingPanel = true;
      defaultPanelSpecs[metric] = {
        type: 'legacy-vega',
        config: {
          ...props,
          panelDefId,
        },
        keyType,
        defaultSection: PANEL_BANK_MEDIA_NAME,
      };
      continue;
    }

    const isMedia = (mediaStrings as string[]).includes(keyType);
    const isChart = ['number', 'histogram'].includes(keyType);

    let defaultSection = PANEL_BANK_CHARTS_NAME;
    // hidden metrics are hidden in the hidden section
    if (hiddenMetrics?.has(metric)) {
      hasMatchingPanel = true;
      defaultSection = PANEL_BANK_HIDDEN_SECTION_NAME;
      // otherwise, if it's a table, it goes in the tables section
    } else if (
      [
        'table-file',
        'incremental-table-file',
        'partitioned-table',
        'joined-table',
        'wb_trace_tree',
      ].includes(keyType)
    ) {
      hasMatchingPanel = true;
      defaultSection = PANEL_BANK_TABLES_NAME;
      // eval-table keys (logged with `_type: eval-table`) get a dedicated
      // Evaluation Tables panel in their own section. Gated by the same ramp
      // flag the old trace-server signal used.
    } else if (keyType === 'eval-table') {
      if (shouldIncludeEvalTablePanels) {
        hasMatchingPanel = true;
        defaultSection = PANEL_BANK_EVALUATION_TABLES_NAME;
      }
      // otherwise, if it's a media panel, default to the media section
    } else if (isMedia) {
      hasMatchingPanel = true;
      defaultSection = PANEL_BANK_MEDIA_NAME;
    }
    // any non hidden chart and media metrics are grouped by prefix
    if (
      (hiddenMetrics?.has(metric) == null ||
        hiddenMetrics?.has(metric) === false) &&
      (isMedia || isChart)
    ) {
      // keys containing certain separators get grouped in sections named after the prefix
      const prefix = getPrefixSectionName(metric, autoOrganizePrefix);

      if (prefix != null) {
        defaultSection = prefix;
      }

      if (!isMedia && defaultSection === 'system') {
        defaultSection = PANEL_BANK_SYSTEM_NAME;
      }
      if (
        keyType === 'histogram' ||
        keyType === 'number' ||
        keyType === 'unknown'
      ) {
        hasMatchingPanel = true;
      }
    }

    /**
     * Catches system metrics which aren't yet directly linked to a system metric template
     * true system metrics won't get a typecount so they'll fail the keytype part of this block
     * https://weightsandbiases.slack.com/archives/C010Y174QGH/p1734394243895049?thread_ts=1734121881.738369&cid=C010Y174QGH
     */
    const isUnknownSystemMetric = isSystemMetric(metric) && !keyType;
    if (isUnknownSystemMetric) {
      hasMatchingPanel = true;
      defaultSection = PANEL_BANK_SYSTEM_NAME;
    }

    // Only add a panel for this metric if we know we support this key type and found a matching panel.
    // Otherwise, we'll end up with a panel for a key type we don't support, which will default to a media panel
    // with an error.
    if (hasMatchingPanel) {
      defaultPanelSpecs[metric] = {
        type: 'default-panel',
        metrics: [metric],
        keyType: keyTypes[metric],
        defaultSection,
        defaultXAxis: definedMetricsMap?.[metric],
      };
    }
  }
  endMeasure({keyCount: keyNames.length});
  return defaultPanelSpecs;
};

export const getExpectedPanelSpecs = (
  keys: RunHistoryKeyInfoKeys,
  viz: VizMap,
  autoOrganizePrefix?: OrganizationPrefix,
  definedMetricsMap?: Panels.MetricsDict,
  hiddenMetrics?: Set<string>,
  shouldIncludeWeavePanel?: boolean,
  shouldIncludeEvalTablePanels?: boolean
) => {
  const defaultSpecs = keyInfoToDefaultPanelSpecs(
    keys,
    viz,
    autoOrganizePrefix,
    definedMetricsMap,
    hiddenMetrics,
    shouldIncludeEvalTablePanels
  );
  const apiAddedSpecs = keyInfoToAddedPanels(
    viz,
    autoOrganizePrefix,
    shouldIncludeWeavePanel
  );

  return {
    defaultSpecs,
    apiAddedSpecs,
  };
};

export function getPrefixSectionName(
  metric: string,
  autoOrganizePrefix?: OrganizationPrefix
): string | null {
  const sepPos = organizationPrefixIsLast(autoOrganizePrefix)
    ? metric.lastIndexOf('/')
    : metric.indexOf('/');
  if (sepPos === -1) {
    return null;
  }
  return metric.slice(0, sepPos);
}

type GetFoundPanelSpecsOptions = {
  shouldIgnorePanel?: (panel: LayedOutPanel) => boolean;
};

export const getFoundPanelSpecsDefault = (
  panelBankPanels: LayedOutPanel[],
  options?: GetFoundPanelSpecsOptions
) => {
  const shouldIgnorePanel =
    options?.shouldIgnorePanel ??
    ((panel: LayedOutPanel) =>
      isSparseOverridesEnabled() && panel.isAuto === false);

  // flatten panels in panel bank into a panel key -> metrics mapping:
  const foundPanelSpecs: FoundPanelSpecs = {};
  // collected merged panel regexes; matching keys should not be auto-generated
  const mergedPanelRegexes: RegExp[] = [];

  for (const panel of panelBankPanels) {
    // By default, sparse overrides skips isAuto=false panels because they are
    // user-created duplicates/custom panels and should not shadow the
    // corresponding auto-generated panel. Callers that can distinguish sparse
    // custom panels from legacy edited panels may pass a narrower predicate.
    if (shouldIgnorePanel(panel)) {
      continue;
    }

    const key = Panels.getKey(panel);
    if (!key) {
      continue;
    }

    if (
      panel.viewType === 'Media Browser' &&
      panel.config.mediaKeys &&
      panel.config.mediaKeys.length === 1
    ) {
      const mediaKey = panel.config.mediaKeys[0];

      foundPanelSpecs[key] = {
        type: 'default-panel',
        metrics: [mediaKey],
        panelId: panel.__id__,
      };

      continue;
    }

    if (panel.viewType === 'Weave') {
      foundPanelSpecs[key] = {
        type: 'default-panel',
        metrics: [key],
        panelId: panel.__id__,
      };

      continue;
    }

    if (panel.viewType === panelViews.evaluationTables) {
      foundPanelSpecs[key] = {
        type: 'default-panel',
        metrics: [key],
        panelId: panel.__id__,
      };

      continue;
    }

    if (panel.viewType === 'Run History Line Plot') {
      if (key) {
        // multi-metric default panels always have an explicit key:
        foundPanelSpecs[key] = {
          type: 'default-panel',
          metrics: panel.config.metrics || [],
          panelId: panel.__id__,
          defaultXAxis: panel.config.startingXAxis,
        };

        // track regex for merged panels
        if (
          panel.config.metricRegex &&
          panel.config.useMetricRegex &&
          panel.config.isMergedPanel
        ) {
          mergedPanelRegexes.push(new RegExp(panel.config.metricRegex));
        }
      }
    }

    if (panel.viewType === 'Vega') {
      if (key) {
        foundPanelSpecs[key] = {
          type: 'legacy-vega',
          viz: panel.config,
          panelId: panel.__id__,
        };
      }
    }

    // any *other* panels (i.e. panels with more than one metric and no
    // explicit key) are assumed to be custom
  }
  return {defaultSpecs: foundPanelSpecs, mergedPanelRegexes};
};

export const getFoundPanelSpecsApiAdded = (
  panelBankPanels: LayedOutPanel[],
  addedPanelSpecs: APIAddedPanelSpecs,
  options?: GetFoundPanelSpecsOptions
) => {
  const shouldIgnorePanel =
    options?.shouldIgnorePanel ??
    ((panel: LayedOutPanel) =>
      isSparseOverridesEnabled() && panel.isAuto === false);

  const foundPanelConfigs: FoundAPIAddedPanelSpecs = {};
  for (const panel of panelBankPanels) {
    if (shouldIgnorePanel(panel)) {
      continue;
    }

    const key = Panels.getKey(panel);
    if (!key || !addedPanelSpecs[key]) {
      continue;
    }
    foundPanelConfigs[key] = {
      config: panel.config,
      panelId: panel.__id__,
      viewType: panel.viewType,
      type: 'api-added-panel',
    };
  }
  return foundPanelConfigs;
};

export const getFoundPanelSpecs = (
  panels: LayedOutPanel[],
  expectedApiAddedPanelSpecs: APIAddedPanelSpecs,
  options?: GetFoundPanelSpecsOptions
) => {
  const {defaultSpecs, mergedPanelRegexes} = getFoundPanelSpecsDefault(
    panels,
    options
  );
  // Test the stacked early return with controlled fixtures where the expected
  // API-added spec map is empty. Duration * 1000 / panelCount gives
  // microseconds per scanned panel. Do not enumerate either spec map solely
  // for telemetry. Measure at the sole production caller so the early return
  // remains covered after the stacked change lands.
  const endMeasure = startFunctionPerformanceMeasure(
    'getFoundPanelSpecsApiAdded'
  );
  const apiAddedSpecs = getFoundPanelSpecsApiAdded(
    panels,
    expectedApiAddedPanelSpecs,
    options
  );
  endMeasure({panelCount: panels.length});
  return {
    defaultSpecs,
    mergedPanelRegexes,
    apiAddedSpecs,
  };
};

/**
 * Compares "expected" panels specs (computed from history keys)
 * against actual panels "found" in the workspace panel bank.
 * Returns list of operations that can be applied to the panel bank
 * to bring it to the expected state.
 */
export const getPanelBankDiff = (
  expected: ReturnType<typeof getExpectedPanelSpecs>,
  found: ReturnType<typeof getFoundPanelSpecs>,
  autoSectionNames: Set<string>,
  prevKeys: string[],
  panelBankConfigState: PanelBankConfigState,
  isPanelGenEnabled = true
): PanelBankDiff => {
  const {endPerfTimer} = startPerfTimer('getPanelBankDiff');
  // Test the stacked first-load optimization with fixtures that have no found
  // default panels, then use steady-state fixtures as a control. Duration *
  // 1000 / expectedDefaultPanelCount gives microseconds per expected panel.
  // The expected-key arrays are required by the diff itself; no spec map is
  // enumerated solely for telemetry.
  const endMeasure = startFunctionPerformanceMeasure('getPanelBankDiff');

  const diff: PanelBankDiff = [];

  const prevKeysSet = new Set(prevKeys);
  const expectedDefaultPanelKeys = Object.keys(expected.defaultSpecs);
  for (const key of expectedDefaultPanelKeys) {
    // skip if expected key is captured by a merged regex panel
    if (found.mergedPanelRegexes.some(regex => regex.test(key))) {
      continue;
    }

    const expectedPanel = expected.defaultSpecs[key];
    const actualPanel = found.defaultSpecs[key];
    const shouldDecompressPanel = autoSectionNames.has(
      expectedPanel.defaultSection
    );

    if (expectedPanel.type === 'legacy-vega') {
      if (!actualPanel) {
        if (isPanelGenEnabled || shouldDecompressPanel) {
          diff.push({
            key,
            operation: {type: 'add', spec: expectedPanel},
          });
        }
      } else if (
        actualPanel.type !== 'legacy-vega' ||
        !isEqual(expectedPanel.config, actualPanel.viz)
      ) {
        diff.push({
          key,
          operation: {
            type: 'update',
            panelId: actualPanel.panelId,
            spec: expectedPanel,
          },
        });
      }

      continue;
    }

    if (actualPanel && actualPanel.type === 'legacy-vega') {
      console.warn(
        `Panel with key '${key}' expected to be non-vega, but was vega`
      );
      continue;
    }

    const metricsInActual = new Set(actualPanel ? actualPanel.metrics : []);

    const metricsDiff = expectedPanel.metrics.filter(
      metric => !metricsInActual.has(metric)
    );

    const expectedDefaultXAxis = expectedPanel.defaultXAxis ?? DEFAULT_X_AXIS;
    const actualDefaultXAxis = actualPanel?.defaultXAxis ?? DEFAULT_X_AXIS;
    if (metricsDiff.length > 0 || expectedDefaultXAxis !== actualDefaultXAxis) {
      // the panel bank is either missing the chart for this key, or it has the
      // chart but needs to add some new metrics to it
      // or update the default x axis
      if (actualPanel) {
        diff.push({
          key,
          operation: {
            type: 'update',
            panelId: actualPanel.panelId,
            spec: {
              type: 'default-panel',
              metrics: metricsDiff,
              defaultXAxis: expectedDefaultXAxis,
            },
          },
        });
      } else if (isPanelGenEnabled || shouldDecompressPanel) {
        // if we've seen this key before and we're *not* reinitializing the panelbank,
        // that means the user just deleted the chart for this key -- we need to hide it
        diff.push({
          key,
          operation: {
            type: 'add',
            spec: {
              ...expectedPanel,
              defaultSection: isHidden(prevKeysSet, key, panelBankConfigState)
                ? PANEL_BANK_HIDDEN_SECTION_NAME
                : expectedPanel.defaultSection,
            },
          },
        });
      }
    }
  }
  // handle panels added through add_panel API, which holds the config in the spec
  const expectedApiAddedPanelKeys = Object.keys(expected.apiAddedSpecs);
  for (const key of expectedApiAddedPanelKeys) {
    const addedPanel = expected.apiAddedSpecs[key];
    const actualPanel = found.apiAddedSpecs[key];
    const shouldDecompressPanel = autoSectionNames.has(
      addedPanel.defaultSection
    );

    if (!actualPanel) {
      if (isPanelGenEnabled || shouldDecompressPanel) {
        diff.push({
          key,
          operation: {
            type: 'add',
            spec: {
              ...addedPanel,
              defaultSection: isHidden(prevKeysSet, key, panelBankConfigState)
                ? PANEL_BANK_HIDDEN_SECTION_NAME
                : addedPanel.defaultSection,
            },
          },
        });
      }
    } else if (
      addedPanel.viewType !== actualPanel.viewType ||
      // the weave-trace panel is capable of handling its own interactive state.
      // changes to the filters should not cause the panel to be regenerated
      // so turning-off the auto-sync which resets the panel config to {} on background refreshes.
      (key !== 'weave-trace' && !isEqual(addedPanel.config, actualPanel.config))
    ) {
      diff.push({
        key,
        operation: {
          type: 'update',
          panelId: actualPanel.panelId,
          spec: addedPanel,
        },
      });
    }
  }
  endMeasure({
    diffCount: diff.length,
    expectedApiAddedPanelCount: expectedApiAddedPanelKeys.length,
    expectedDefaultPanelCount: expectedDefaultPanelKeys.length,
    mergedPanelRegexCount: found.mergedPanelRegexes.length,
    prevKeyCount: prevKeys.length,
  });
  endPerfTimer();
  return diff;
};

export function generateSystemPanel(
  key: keyof typeof Panels.systemPanelTemplates,
  metrics: string[]
) {
  const template = Panels.systemPanelTemplates[key];

  return {
    key,
    viewType: 'Run History Line Plot',
    config: {
      metrics,
      groupBy: 'None',
      chartTitle: template.yAxis,
      yAxisMin: template.percentage ? 0 : undefined,
      yAxisMax: template.percentage ? 100 : undefined,
    } as RunsLinePlotConfig,
  };
}

export function getDefaultPanelConfigInner(
  keyName: string,
  addPanelSpec: AddPanel['spec']
): LayedOutPanel {
  if (addPanelSpec.type === 'legacy-vega') {
    return {
      key: keyName,
      viewType: 'Vega',
      config: addPanelSpec.config,
    } as LayedOutPanel;
  } else if (addPanelSpec.type === 'api-added-panel') {
    return {
      key: keyName,
      viewType: addPanelSpec.viewType,
      config: addPanelSpec.config,
    } as LayedOutPanel;
  }

  if (addPanelSpec.type === 'regex-panel') {
    return {
      viewType: 'Run History Line Plot',
      config: {
        chartTitle: addPanelSpec.metricRegex,
        metricRegex: addPanelSpec.metricRegex,
        useMetricRegex: true,
        groupBy: 'None',
        legendFields: ['run:displayName'],
        isMergedPanel: addPanelSpec.isMergedPanel,
      },
    } as LayedOutPanel;
  }

  const metrics = addPanelSpec.metrics.sort();
  const openMetricsPrefix = 'system/openmetrics.';
  if (keyName.startsWith(openMetricsPrefix)) {
    return {
      key: keyName,
      viewType: 'Run History Line Plot',
      config: {
        metrics,
        groupBy: 'None',
        chartTitle: keyName.slice(
          openMetricsPrefix.length,
          keyName.lastIndexOf('.')
        ),
        yAxisMin: undefined,
        yAxisMax: undefined,
      } as RunsLinePlotConfig,
    } as LayedOutPanel;
  }
  if (keyName in Panels.systemPanelTemplates) {
    return generateSystemPanel(
      keyName as keyof typeof Panels.systemPanelTemplates,
      metrics
    ) as LayedOutPanel;
  }

  if (
    [
      'table-file',
      'incremental-table-file',
      'partitioned-table',
      'joined-table',
      'wb_trace_tree',
    ].includes(addPanelSpec.keyType)
  ) {
    return {
      viewType: 'Weave',
      config: weavePanelForWeaveKey(keyName, addPanelSpec.keyType),
    } as LayedOutPanel;
  }

  if (addPanelSpec.keyType === 'eval-table') {
    return {
      key: keyName,
      viewType: panelViews.evaluationTables,
      config: {evalName: keyName},
    } as LayedOutPanel;
  }

  return {
    viewType:
      addPanelSpec.keyType === 'histogram' ||
      addPanelSpec.keyType === 'number' ||
      addPanelSpec.keyType === 'unknown'
        ? 'Run History Line Plot'
        : 'Media Browser',
    config:
      addPanelSpec.keyType === 'histogram' ||
      addPanelSpec.keyType === 'number' ||
      addPanelSpec.keyType === 'unknown'
        ? {
            metrics: [keyName],
            groupBy: 'None',
            legendFields: ['run:displayName'],
            startingXAxis: addPanelSpec.defaultXAxis,
          }
        : {
            mediaKeys: [keyName],
          },
  } as LayedOutPanel;
}

export function getDefaultPanelConfig(
  keyName: string,
  addPanelSpec: AddPanel['spec'],
  isAuto = false
): LayedOutPanel {
  return {
    ...getDefaultPanelConfigInner(keyName, addPanelSpec),
    __id__: ID(),
    isAuto: isAuto,
  };
}

// the panel in this function is expected to come from Immer:
// we change it in place instead of returning a new panel
export function addMetricsToPanelConfig(
  mutablePanel: LayedOutPanel,
  newMetrics: string[]
) {
  // NOTE: this, and the diff infrastructure, could probably be adapted to
  // other kinds of plots in the future with a few changes
  if (
    mutablePanel.viewType !== 'Run History Line Plot' ||
    newMetrics.length === 0
  ) {
    return;
  }

  mutablePanel.config.metrics = [
    ...(mutablePanel.config.metrics || []),
    ...newMetrics,
  ];
}

export function addMetricsToPanelConfigImmutable(
  panel: LayedOutPanel,
  newMetrics: string[]
): LayedOutPanel {
  // NOTE: this, and the diff infrastructure, could probably be adapted to
  // other kinds of plots in the future with a few changes
  if (panel.viewType !== 'Run History Line Plot' || newMetrics.length === 0) {
    return panel;
  }

  return {
    ...panel,
    config: {
      ...panel.config,
      metrics: [...(panel.config.metrics ?? []), ...newMetrics],
    },
  };
}

export function updateDefaultxAxis(
  mutablePanel: LayedOutPanel,
  newXAxis?: string
) {
  if (mutablePanel.viewType !== 'Run History Line Plot') {
    return;
  }
  mutablePanel.config.startingXAxis = newXAxis;
}

export function updateDefaultxAxisImmutable(
  panel: LayedOutPanel,
  newXAxis?: string
): LayedOutPanel {
  if (panel.viewType !== 'Run History Line Plot') {
    return panel;
  }
  return {
    ...panel,
    config: {
      ...panel.config,
      startingXAxis: newXAxis,
    },
  };
}

// Returns the array of keys that are present in panelBankConfig
export function getInitializedKeys(panelBankConfig: PanelBankConfig): string[] {
  // console.time('initializedkeys');
  const initializedKeys = compact(
    panelBankConfig.sections.flatMap(s =>
      compact(s.panels.map(p => Panels.getKey(p)))
    )
  );

  // console.timeEnd('initializedkeys');
  return initializedKeys;
}

// Returns the array of keys that aren't yet present in panelBankConfig
export function getUninitializedKeys(
  initializedKeysLookup: {[key: string]: 1},
  keyInfo: RunHistoryKeyInfo
) {
  const uninitializedKeys = Object.keys(keyInfo.keys).filter(
    k => !isReservedKey(k) && initializedKeysLookup[k] == null
  );
  return uninitializedKeys;
}

const alwaysHaveDataPanelTypes = toIncludesObj([
  'Markdown Panel',
  'Parallel Coordinates Plot',
  'Scatter Plot',
  'Parameter Importance',
  'Run Comparer',
  'Code Comparer',
  'Bar Chart',
]);

// Returns true if we have the data we need to render this panel
export function haveDataForPanel(
  historyKeyInfo: RunHistoryKeyInfo,
  panel: Panel
): boolean {
  if (alwaysHaveDataPanelTypes[panel.viewType]) {
    return true;
  }
  if (panel.viewType === 'Vega2') {
    return haveDataForVega2Panel(historyKeyInfo, panel.config.userQuery);
  } else {
    const metrics = Panels.getMetrics(panel);
    // this used to check all metrics to exist in order to consider
    // that run's historyKey "have data" for panel.
    // now, it only checks partial keys to exist, changed
    // in https://github.com/wandb/core/pull/8978
    const haveAtLeastOneMetric = metrics.some(
      metric => historyKeyInfo.keys[metric] != null
    );
    // if panel's metrics is empty, we display it so users can config the panel
    if (metrics.length > 0 && !haveAtLeastOneMetric) {
      // run history line plots suppoprt y axes metrics defined with a regex,
      // in which case we need to check if any of the keys match the regex. The
      // metrics matching the regex should be populated in panel.config.metrics
      // by react hooks before this function is called, but the panel state is
      // frequently out of sync, so we need to check for this case here until
      // the state issue is fixed.
      if (panel.viewType === 'Run History Line Plot') {
        if (panel.config.metricRegex != null && panel.config.useMetricRegex) {
          const regex = new RegExp(panel.config.metricRegex);
          // Prefer the server-provided sortedKeys (a pre-existing array) over
          // Object.keys(historyKeyInfo.keys), which allocates an O(keys) array
          // on every call. Order is irrelevant for an any-match check, and the
          // two hold the same key set. Mirrors getMetricKeys in
          // PanelRunsLinePlot/utils/getSelectedMetrics.ts.
          const keys =
            historyKeyInfo.sortedKeys.length > 0
              ? historyKeyInfo.sortedKeys
              : Object.keys(historyKeyInfo.keys);
          for (const key of keys) {
            if (regex.test(key)) {
              return true;
            }
          }
        }
      }
      return false;
    }
  }
  return true;
}

export function haveDataForVega2Panel(
  historyKeyInfo: RunHistoryKeyInfo,
  userQuery: VegaPanel2Config['userQuery']
): boolean {
  let queryArgs: Array<undefined | QueryArgSpecs>;
  if (userQuery != null) {
    queryArgs = userQuery.queryFields[0].fields
      // we filter out the config and summary keys, since we don't have an easy
      // way to retrieve all of the configs and summaries for all of the runs
      .filter(
        (field): field is QueryField =>
          field.args != null &&
          field.name !== 'config' &&
          field.name !== 'summary'
      )
      .map(field => field.args) as Array<undefined | QueryArgSpecs>;
  } else {
    return true;
  }
  for (const queryArg of queryArgs) {
    if (queryArg == null) {
      continue;
    }
    const hasData: boolean = queryArg.every(arg => {
      if (arg.name === 'tableColumns') {
        return true;
      }
      if (arg.name === 'tableKey') {
        return historyKeyInfo.keys[arg.value] != null;
      }
      return (
        !Array.isArray(arg.value) ||
        arg.value
          .filter(isTruthy)
          .every(key => historyKeyInfo.keys[key] != null)
      );
    });
    if (!hasData) {
      return false;
    }
  }
  return true;
}

const alwaysMatchPanelTypes = toIncludesObj([
  'Markdown Panel',
  'Parameter Importance',
  'Run Comparer',
  'Code Comparer',
]);

export function searchQueryMatchesPanel(searchQuery: string, panel: Panel) {
  if (alwaysMatchPanelTypes[panel.viewType]) {
    return true;
  }
  const searchRegex = searchRegexFromQuery(searchQuery);
  if (searchRegex == null) {
    return true;
  }
  return searchRegexMatchesPanel(searchRegex, panel);
}

const panelSearchStrings = (panel: Panel): string[] => {
  const searchStrings = [...Panels.getMetrics(panel)];

  if ('chartTitle' in panel.config && panel.config.chartTitle != null) {
    searchStrings.push(panel.config.chartTitle);
  }

  for (const settingsProp of [`fieldSettings`, `stringSettings`]) {
    if (
      settingsProp in panel.config &&
      (panel.config as any)[settingsProp] != null
    ) {
      const settings = (panel.config as any)[settingsProp];
      const title = settings.title || settings.Title;
      if (title) {
        searchStrings.push(title);
      }
    }
  }

  return compact(searchStrings);
};

export function searchRegexMatchesPanel(regex: RegExp, panel: Panel) {
  if (alwaysMatchPanelTypes[panel.viewType]) {
    return true;
  }

  return panelSearchStrings(panel).some(searchString =>
    regex.test(searchString)
  );
}

export function hideSearchRegexMatchesPanel(regex: RegExp, panel: Panel) {
  return panelSearchStrings(panel).some(searchString =>
    regex.test(searchString)
  );
}

export const panelSortingKeyFromPanel = (panel: Panel) => {
  if ('chartTitle' in panel.config) {
    return panel.config.chartTitle;
  }
  if (
    'mediaKeys' in panel.config &&
    panel.config.mediaKeys != null &&
    panel.config.mediaKeys?.length > 0
  ) {
    return panel.config.mediaKeys[0];
  }
  if (
    'metrics' in panel.config &&
    panel.config.metrics != null &&
    panel.config.metrics.length > 0
  ) {
    return panel.config.metrics[0];
  }
  if ('key' in panel && panel.key != null) {
    return panel.key;
  }
  return undefined;
};

export function sortPanelCheck(
  keyA: string | undefined,
  keyB: string | undefined
): number {
  if (keyA == null && keyB == null) {
    return 0;
  }
  if (keyA == null) {
    return -1;
  }
  if (keyB == null) {
    return 1;
  }
  if (isNumber(keyA) && isNumber(keyB)) {
    return keyA - keyB;
  }
  return keyA.toLowerCase() > keyB.toLowerCase() ? 1 : -1;
}

// TODO: move this function into SearchProvider, and handle the error there by setting error state and displaying a message
export function searchRegexFromQuery(searchQuery: string): RegExp | null {
  let searchRegex: RegExp | null = null;
  let query = searchQuery.trim();
  // if the query is a single '*', match everything (even though * isn't technically a valid regex)
  if (query === '*') {
    query = '.*';
  }
  if (query.length > 0) {
    try {
      searchRegex = new RegExp(query, 'i');
    } catch {
      // console.warn(`Invalid search query: ${query}`);
      // since we execute search onChange, we need to catch invalid/incomplete regexes, like '[' without closing ']'
    }
  }
  return searchRegex;
}

export function isHistogram(
  panel: Panel,
  historyKeyInfo: RunHistoryKeyInfo
): boolean {
  const keyTypes = RunHelpers.keyTypes(historyKeyInfo.keys);
  return isHistogramWithKeyTypes(panel, keyTypes);
}

export function isHistogramWithKeyTypes(
  panel: Panel,
  keyTypes: {[key: string]: RunHistoryKeyType}
): boolean {
  const panelKey = Panels.getKey(panel);
  return panelKey != null && keyTypes[panelKey] === 'histogram';
}

// TODO: maybe worth a refactor. instead of testing and returning a boolean, we may want to add metadata to the panel,
//  e.g. panel.isSearchMatch, panel.isHistogram, panel.hasData, etc.
export function panelIsActive({
  section,
  panel,
  isSingleRun,
  searchRegex,
  hideSearchRegex,
  historyKeyInfo,
  keyTypes,
}: PanelIsActiveParams): boolean {
  // Don't show histograms in multi-run workspaces
  if (!isSingleRun) {
    const hist =
      keyTypes != null
        ? isHistogramWithKeyTypes(panel, keyTypes)
        : isHistogram(panel, historyKeyInfo);
    if (hist) {
      return false;
    }
  }

  if (searchRegex != null && !searchRegexMatchesPanel(searchRegex, panel)) {
    return false;
  }

  if (
    hideSearchRegex != null &&
    hideSearchRegexMatchesPanel(hideSearchRegex, panel)
  ) {
    return false;
  }

  if (section.type !== 'grid' && !haveDataForPanel(historyKeyInfo, panel)) {
    return false;
  }

  return true;
}

export function getSectionsWithVisiblePanels(
  sections: readonly PanelBankSectionConfig[],
  mappingFn: (
    s: PanelBankSectionConfig
  ) => PanelBankSectionConfigWithVisiblePanels = defaultSectionToSectionWithVisiblePanelsMapping
): PanelBankSectionConfigWithVisiblePanels[] {
  return sections.map(mappingFn).filter(s => s.visiblePanels.length > 0);
}

export function defaultSectionToSectionWithVisiblePanelsMapping(
  s: PanelBankSectionConfig
): PanelBankSectionConfigWithVisiblePanels {
  return {
    ...s,
    ref: (s as any).ref,
    visiblePanels: [...s.panels],
  };
}
